import { useCallback, useEffect, useRef, useState } from 'react';

export type AutosaveStatus = 'idle' | 'saving' | 'saved' | 'error';

/**
 * Debounced autosave hook. Watches `value` and calls `saveFn` after a
 * configurable delay. Skips the first value after `enabled` flips to true
 * (the "load" render) so only subsequent user-driven changes trigger a save.
 */
export function useAutosave<T>(
  value: T,
  saveFn: (value: T) => Promise<void>,
  options: { delayMs?: number; enabled?: boolean } = {},
): { status: AutosaveStatus; error: string | null } {
  const { delayMs = 800, enabled = true } = options;
  const [status, setStatus] = useState<AutosaveStatus>('idle');
  const [error, setError] = useState<string | null>(null);

  const initializedRef = useRef(false);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const fadeTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const saveFnRef = useRef(saveFn);
  const valueRef = useRef(value);

  saveFnRef.current = saveFn;
  valueRef.current = value;

  const doSave = useCallback(async () => {
    clearTimeout(fadeTimerRef.current);
    setStatus('saving');
    setError(null);
    try {
      await saveFnRef.current(valueRef.current);
      setStatus('saved');
      fadeTimerRef.current = setTimeout(() => setStatus('idle'), 2000);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Save failed';
      setError(msg);
      setStatus('error');
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;

    if (!initializedRef.current) {
      initializedRef.current = true;
      return;
    }

    clearTimeout(saveTimerRef.current);
    clearTimeout(fadeTimerRef.current);

    if (delayMs === 0) {
      void doSave();
    } else {
      setStatus('idle');
      saveTimerRef.current = setTimeout(() => void doSave(), delayMs);
    }

    return () => clearTimeout(saveTimerRef.current);
  }, [value, enabled, delayMs, doSave]);

  useEffect(() => {
    return () => {
      clearTimeout(saveTimerRef.current);
      clearTimeout(fadeTimerRef.current);
    };
  }, []);

  return { status, error };
}
